
import React, { useState } from "react";
import { Loader2 } from "lucide-react";
import { formatCurrency } from "@/lib/utils";
import { Card } from "@/components/ui/card";
import AddBridgeItemDialog from "./AddBridgeItemDialog";
import DeleteBridgeItemDialog from "./DeleteBridgeItemDialog";
import EditBridgeItemDialog from "./EditBridgeItemDialog";
import { WaterfallChart } from "./bridge-plan/WaterfallChart";
import { SliderControl } from "./bridge-plan/SliderControl";
import { RevenueSummaryCard } from "./bridge-plan/RevenueSummaryCard";
import { BridgeComponentsSection } from "./bridge-plan/BridgeComponentsSection";
import { useBridgeData } from "./bridge-plan/hooks/useBridgeData";
import { useBridgeOperations } from "./bridge-plan/hooks/useBridgeOperations";
import { useGoogleCharts } from "./bridge-plan/hooks/useGoogleCharts";
import { BridgeDataItem } from "./bridge-plan/types";

interface BridgePlanTabProps {
  className?: string;
}

const BridgePlanTab: React.FC<BridgePlanTabProps> = ({ className }) => {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState<boolean>(false);
  const [deleteDialogState, setDeleteDialogState] = useState<{
    open: boolean;
    item: BridgeDataItem | null;
  }>({
    open: false,
    item: null
  });
  const [editDialogState, setEditDialogState] = useState<{
    open: boolean;
    item: BridgeDataItem | null;
  }>({
    open: false,
    item: null
  });
  const [sliderValue, setSliderValue] = useState<number>(95);

  const { bridgeData, isLoading, error, refreshData } = useBridgeData();
  const { googleChartsLoaded } = useGoogleCharts();
  const { handleAddBridgeItem, handleEditBridgeItem, handleDeleteBridgeItem } = useBridgeOperations(refreshData);

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSliderValue(parseInt(e.target.value));
  };

  const handleDeleteConfirm = async () => {
    if (!deleteDialogState.item || !deleteDialogState.item.id) return;
    await handleDeleteBridgeItem(deleteDialogState.item.id);
  };

  // Calculate revenue summary values
  const ytdRevenueItem = bridgeData.find(item => item.isStart);
  const totalBridgedItem = bridgeData.find(item => item.isTotalBridged);
  const targetRevenueItem = bridgeData.find(item => item.isTarget);
  const ytdRevenue = ytdRevenueItem?.value || 0;
  const totalBridgedRevenue = totalBridgedItem?.value || 0;
  const targetRevenue = targetRevenueItem?.value || 0;
  const revenueDifference = totalBridgedRevenue - targetRevenue;
  const differencePercentage = targetRevenue > 0 ? revenueDifference / targetRevenue * 100 : 0;
  const isPositiveDifference = revenueDifference >= 0;

  return (
    <div className={`text-white space-y-4 ${className || ''}`}>
      <div className="mb-3">
        <h2 className="font-bold mb-1 text-base">Revenue Bridge Plan</h2>
        <p className="text-primary-light text-xs">
          This waterfall chart visualizes how various sales initiatives can bridge the gap between current YTD revenue and target revenue.
        </p>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-primary-light" />
        </div>
      ) : error ? (
        <Card className="bg-secondary-dark/50 border-red-800/30 p-4">
          <p className="text-red-400">{error}</p>
        </Card>
      ) : (
        <>
          <div className="flex flex-col lg:flex-row gap-3">
            <SliderControl 
              sliderValue={sliderValue}
              onSliderChange={handleSliderChange}
            />
            
            <WaterfallChart 
              bridgeData={bridgeData}
              sliderValue={sliderValue}
              googleChartsLoaded={googleChartsLoaded}
            />
            
            <RevenueSummaryCard 
              ytdRevenueItem={ytdRevenueItem}
              totalBridgedItem={totalBridgedItem}
              targetRevenueItem={targetRevenueItem}
              revenueDifference={revenueDifference}
              differencePercentage={differencePercentage}
              isPositiveDifference={isPositiveDifference}
            />
          </div>

          <BridgeComponentsSection 
            bridgeData={bridgeData}
            onAddClick={() => setIsAddDialogOpen(true)}
            onEditClick={(item) => setEditDialogState({ open: true, item })}
            onDeleteClick={(item) => setDeleteDialogState({ open: true, item })}
          />
          
          <AddBridgeItemDialog 
            open={isAddDialogOpen} 
            onClose={() => setIsAddDialogOpen(false)} 
            onAdd={handleAddBridgeItem} 
          />
          
          <DeleteBridgeItemDialog 
            open={deleteDialogState.open} 
            componentName={deleteDialogState.item?.name || ""} 
            onClose={() => setDeleteDialogState({ open: false, item: null })} 
            onConfirm={handleDeleteConfirm} 
          />

          <EditBridgeItemDialog 
            open={editDialogState.open} 
            item={editDialogState.item} 
            onClose={() => setEditDialogState({ open: false, item: null })} 
            onEdit={handleEditBridgeItem} 
          />
        </>
      )}
    </div>
  );
};

export default BridgePlanTab;
